/* eslint-disable sonarjs/cognitive-complexity */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { BOOKING_CALENDAR } from '../../../../brands/default/content/components/deal';
import { useAvailability, isNextMonth } from '../../../../helpers/booking';
import Spinner from '../../../_generic/spinner/Spinner';
import BookingCalendarCells from './BookingCalendarCells';
import BookingCalendarCellsBeginEnd from './BookingCalendarCellsBeginEnd';
import BookingCalendarCheckInOut from './BookingCalendarCheckInOut';
import BookingCalendarNav from './BookingCalendarNav';
import BookingCalendarNavTop from './BookingCalendarNavTop';

const BookingCalendarOnRedemption = ({
  beginEndDates,
  deal,
  selection,
  onSelect,
  onSelectEndDate,
  onUpdateLastAvailableStay,
  onUpdateLastSelectableDate,
  quantity,
  departureAirport,
  nights,
  isReadyForCalendar,
  thisDealLondonAirports,
  isBookingCalendarOnRedemption,
  resetCalendar,
}) => {
  // calendar view selection
  const [month, setMonth] = useState();
  const [year, setYear] = useState();
  const [currentHoverDate, setCurrentHoverDate] = useState(null);
  const [isMouseLeftCalendar, setIsMouseLeftCalendar] = useState(true);

  // cells to render
  const {
    cells,
    next,
    prev,
    displayMonth,
    displayYear,
    lastAvailableStay,
    lastSelectableDate,
    lastSelectableDateFormatted,
    isCheckinNearMonthEnd,
  } = useAvailability(
    deal,
    month,
    year,
    selection,
    quantity,
    departureAirport,
    nights,
    isReadyForCalendar,
    thisDealLondonAirports,
    isBookingCalendarOnRedemption,
    beginEndDates,
    currentHoverDate,
    isMouseLeftCalendar,
  );

  useEffect(() => {
    if (onUpdateLastSelectableDate) {
      onUpdateLastSelectableDate(lastSelectableDate);
    }
  }, [lastSelectableDate]);

  useEffect(() => {
    if (onUpdateLastAvailableStay) {
      onUpdateLastAvailableStay(lastAvailableStay);
    }
  }, [lastAvailableStay]);

  const handleOnHoverDate = (date) => {
    setCurrentHoverDate(date);
    setIsMouseLeftCalendar(false);
  };

  const handleOnHoverOffCells = () => {
    setIsMouseLeftCalendar(true);
  };

  if (!cells) {
    return (
      <>
        <div className="booking-calendar--loading">
          <Spinner />
        </div>
        <style jsx>{`
          .booking-calendar--loading {
            position: relative;
            min-height: 480px; /* 5 row calendar (most common) */
          }
        `}</style>
      </>
    );
  }

  const onNext = () => {
    if (next) {
      setMonth(next.month);
      setYear(next.year);
    }
  };
  const onPrevious = () => {
    if (prev) {
      setMonth(prev.month);
      setYear(prev.year);
    }
  };

  const onMonthChange = (key) => {
    const isNext = isNextMonth(key, displayMonth);

    if (isNext) {
      onNext();
    } else {
      onPrevious();
    }
  };

  return (
    <>
      <BookingCalendarNavTop />
      {beginEndDates && (
        <>
          <BookingCalendarCheckInOut
            beginEndDates={beginEndDates}
            lastSelectableDateFormatted={lastSelectableDateFormatted}
            onSelect={onSelectEndDate}
            resetCalendar={resetCalendar}
          />
        </>
      )}
      <BookingCalendarNav
        disabledNext={!next}
        disabledPrev={!prev}
        isCheckinNearMonthEnd={isCheckinNearMonthEnd}
        month={displayMonth}
        next={next}
        onNext={onNext}
        onPrev={onPrevious}
        prev={prev}
        year={displayYear}
      />

      {!beginEndDates && (
        <BookingCalendarCells
          cells={cells}
          isBookingCalendarOnRedemption={isBookingCalendarOnRedemption}
          onMonthChange={onMonthChange}
          onSelect={onSelect}
        />
      )}
      {beginEndDates && (
        <BookingCalendarCellsBeginEnd
          beginEndDates={beginEndDates}
          cells={cells}
          onHoverDate={handleOnHoverDate}
          onHoverOffCells={handleOnHoverOffCells}
          onMonthChange={onMonthChange}
          onSelect={onSelect}
        />
      )}
      {!isBookingCalendarOnRedemption && (
        <p className="booking-calendar__asterix">
          *{BOOKING_CALENDAR.pricesShownLowest}
        </p>
      )}
      {isBookingCalendarOnRedemption && (
        <p className="booking-calendar__asterix">
          {BOOKING_CALENDAR.bookableAtRedemption}
        </p>
      )}

      <style jsx>{`
        .booking-calendar__asterix {
          font-size: 12px;
          margin-bottom: 20px;
        }
      `}</style>
    </>
  );
};

BookingCalendarOnRedemption.propTypes = {
  beginEndDates: PropTypes.object,
  deal: PropTypes.object,
  departureAirport: PropTypes.string,
  isBookingCalendarOnRedemption: PropTypes.bool,
  isReadyForCalendar: PropTypes.bool.isRequired,
  nights: PropTypes.string,
  onSelect: PropTypes.func,
  onSelectEndDate: PropTypes.func,
  onUpdateLastAvailableStay: PropTypes.func,
  onUpdateLastSelectableDate: PropTypes.func,
  quantity: PropTypes.number,
  resetCalendar: PropTypes.func,
  selection: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  thisDealLondonAirports: PropTypes.array,
};

BookingCalendarOnRedemption.defaultProps = {
  deal: {},
  isBookingCalendarOnRedemption: false,
  selection: null,
  thisDealLondonAirports: null,
};

export default BookingCalendarOnRedemption;
